/**
 * Card Payment Form with 3DS Challenge Support
 * Handles card payments with client-side tokenization and 3DS challenges
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ThreeDSChallengeModal } from './ThreeDSChallengeModal';
import { CoinflowTokenization } from '@/lib/coinflow-tokenization';
import { getDeviceId } from '@/lib/nsure';
import { COINFLOW_PRODUCT_TYPE, COINFLOW_PRODUCT_DESCRIPTION } from '@/lib/coinflow-tokenization';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { CreditCard, Lock } from 'lucide-react';
import { CoinflowCardNumberInput, CoinflowCvvInput, CoinflowCardTokenResponse, CoinflowEnvs } from '@coinflowlabs/react';

interface ChallengeData {
  transactionId: string;
  url: string;
  creq: string;
}

interface CardPaymentFormProps {
  amount: number; // in cents
  packageId: string;
  scheduledOfferId?: string;  // For scheduled offer purchases
  packageTitle: string;
  promoCode?: string;
  promoBonusGC?: number;
  promoBonusSC?: number;
  onSuccess: (paymentId: string, data: { isFirstPurchase?: boolean }) => void;
  onError: (error: string | Error) => void;
  /** Checkout session transaction ID - if provided, reuses this transaction instead of creating a new one */
  checkoutSessionId?: number | null;
}

export const CardPaymentForm: React.FC<CardPaymentFormProps> = ({
  amount,
  packageId,
  scheduledOfferId,
  packageTitle,
  promoCode,
  promoBonusGC,
  promoBonusSC,
  onSuccess,
  onError,
  checkoutSessionId,
}) => {
  const isScheduledOffer = !!scheduledOfferId;
  const [cardData, setCardData] = useState({
    number: '',
    cvv: '',
    expMonth: '',
    expYear: '',
    zipCode: '',
  });
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  });
  const [saveCard, setSaveCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [challengeData, setChallengeData] = useState<ChallengeData | null>(null);
  const [cardToken, setCardToken] = useState<string | null>(null);


  const merchantId = import.meta.env.VITE_COINFLOW_MERCHANT_ID || '';
  const environment = import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'prod' as CoinflowEnvs : 'sandbox' as CoinflowEnvs;

  // Refs for Coinflow React SDK components
  const coinflowCardNumberRef = useRef<{ getToken(): Promise<CoinflowCardTokenResponse> } | null>(null);
  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const cvvWrapperRef = useRef<HTMLDivElement>(null);

  // Validate Coinflow configuration
  if (!merchantId) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <p className="text-destructive font-semibold">Payment Configuration Error</p>
            <p className="text-sm text-muted-foreground">
              Coinflow merchant ID is not configured. Please contact support.
            </p>
            <p className="text-xs text-muted-foreground">
              Error: VITE_COINFLOW_MERCHANT_ID is missing
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }


  /**
   * Initiate checkout with 3DS parameters
   * Handles both frictionless and challenged flows
   */
  const initiateCheckout = async (token: string) => {
    const checkoutData = {
      packageId: isScheduledOffer ? undefined : packageId,
      scheduledOfferId: isScheduledOffer ? scheduledOfferId : undefined,
      paymentMethod: 'card' as const,
      token, // Card token from CoinflowCardNumberInput
      customerInfo: {
        ...billingInfo,
        cardExpYear: cardData.expYear,
        cardExpMonth: cardData.expMonth,
      },
      saveCard,
      deviceId: getDeviceId(),
      promoCode: promoCode || undefined,
      promoBonusGc: promoBonusGC || 0,
      promoBonusSc: promoBonusSC || 0,
      chargebackProtectionData: [
        {
          productName: 'package',
          productType: COINFLOW_PRODUCT_TYPE,
          quantity: 1,
          rawProductData: {
            description: COINFLOW_PRODUCT_DESCRIPTION,
            packageId,
            packageTitle,
            amount,
          },
        },
      ],
      authentication3DS: CoinflowTokenization.get3DSParams(),
      checkoutSessionId: checkoutSessionId || undefined, // Reuse existing checkout session if available
    };

    try {
      const response = await apiClient.post('/payments/checkout', checkoutData);

      // ✅ Frictionless 3DS - payment completed
      if (response.externalPaymentId) {
        return { 
          success: true, 
          paymentId: response.externalPaymentId,
          isFirstPurchase: response.isFirstPurchase,
        };
      }

      return { success: false };
    } catch (error: any) {
      // 412 = Challenge Required (matches Coinflow's API behavior)
      // error.status is the HTTP status code (412)
      // error.response is the parsed JSON response body from NestJS
      if (error.status === 412 || error.response?.requiresAction) {
        const actionData = error.response?.actionData || error.response;
        
        if (actionData?.type === '3ds_challenge') {
          return {
            challenge: true,
            challengeData: {
              transactionId: actionData.transactionId,
              creq: actionData.creq,
              url: actionData.url,
            },
          };
        }
      }
      throw error;
    }
  };

  /**
   * Complete checkout after 3DS challenge
   */
  const completeCheckoutAfterChallenge = async (transactionId: string) => {
    if (!cardToken) {
      throw new Error('Card token missing');
    }

    const checkoutData = {
      packageId: isScheduledOffer ? undefined : packageId,
      scheduledOfferId: isScheduledOffer ? scheduledOfferId : undefined,
      paymentMethod: 'card' as const,
      token: cardToken, // Card token for retry after 3DS challenge
      customerInfo: {
        ...billingInfo,
        cardExpYear: cardData.expYear,
        cardExpMonth: cardData.expMonth,
      },
      saveCard,
      deviceId: getDeviceId(),
      promoCode: promoCode || undefined,
      promoBonusGc: promoBonusGC || 0,
      promoBonusSc: promoBonusSC || 0,
      chargebackProtectionData: [
        {
          productName: 'package',
          productType: COINFLOW_PRODUCT_TYPE,
          quantity: 1,
          rawProductData: {
            description: COINFLOW_PRODUCT_DESCRIPTION,
            packageId,
            packageTitle,
            amount,
          },
        },
      ],
      challengeTransactionId: transactionId,
      checkoutSessionId: checkoutSessionId || undefined, // Reuse existing checkout session if available
    };

    try {
      const response = await apiClient.post('/payments/checkout', checkoutData);

      if (response.externalPaymentId) {
        return { 
          success: true, 
          paymentId: response.externalPaymentId,
          isFirstPurchase: response.isFirstPurchase,
        };
      }

      throw new Error('Payment completion failed');
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Step 1: Tokenize card using Coinflow React SDK components (client-side - PCI compliant)
      if (!coinflowCardNumberRef.current) {
        throw new Error('Card input component not initialized');
      }

      // Get token from card number input (includes CVV if CVV input is used)
      const cardTokenResponse = await coinflowCardNumberRef.current.getToken();
      if (!cardTokenResponse.token) {
        throw new Error('Failed to tokenize card number');
      }
      
      // Use the card number token (CVV is included when CoinflowCvvInput is used)
      const token = cardTokenResponse.token;
      setCardToken(token);

      // Step 2: Initiate checkout with 3DS params
      const result = await initiateCheckout(token);

      if (result.success) {
        // Frictionless 3DS - payment completed
        onSuccess(result.paymentId!, { isFirstPurchase: result.isFirstPurchase });
        setIsProcessing(false);
        return;
      }

      if (result.challenge) {
        // Challenge required - show 3DS modal
        setChallengeData(result.challengeData);
        setIsProcessing(false);
        return;
      }

    } catch (error: any) {
      setIsProcessing(false);
      // Pass the full error object so error code can be extracted
      const errorObj = error.response?.error 
        ? new Error(error.response?.message || error.message || 'Payment failed')
        : error;
      (errorObj as any).code = error.response?.error || error.code;
      (errorObj as any).response = error.response;
      onError(errorObj);
    }
  };

  const handleChallengeComplete = useCallback(
    async (transactionId: string) => {
      setIsProcessing(true);
      setChallengeData(null); // Close challenge modal

      try {
        // Complete checkout with challenge transactionId
        const result = await completeCheckoutAfterChallenge(transactionId);

        if (result.success) {
          onSuccess(result.paymentId!, { isFirstPurchase: result.isFirstPurchase });
        }
      } catch (error: any) {
        // Pass the full error object so error code can be extracted
        const errorObj = error.response?.error 
          ? new Error(error.response?.message || error.message || 'Payment completion failed')
          : error;
        (errorObj as any).code = error.response?.error || error.code;
        (errorObj as any).response = error.response;
        onError(errorObj);
      } finally {
        setIsProcessing(false);
      }
    },
    [cardToken, packageId, scheduledOfferId, isScheduledOffer, billingInfo, cardData, saveCard, promoCode, onSuccess, onError],
  );


  // Handle focus events from TokenEx iframe to show focus ring
  useEffect(() => {
    const handleFocus = (event: MessageEvent) => {
      if (event.data?.event === 'focus' || event.data?.event === 'cvvFocus') {
        const container = event.data?.container;
        if (container === 'tokenExCardNumber' && cardWrapperRef.current) {
          cardWrapperRef.current.classList.add('focused');
        } else if (container === 'tokenExCardCvv' && cvvWrapperRef.current) {
          cvvWrapperRef.current.classList.add('focused');
        }
      }
    };

    const handleBlur = (event: MessageEvent) => {
      if (event.data?.event === 'blur' || event.data?.event === 'cvvBlur') {
        const container = event.data?.container;
        if (container === 'tokenExCardNumber' && cardWrapperRef.current) {
          cardWrapperRef.current.classList.remove('focused');
        } else if (container === 'tokenExCardCvv' && cvvWrapperRef.current) {
          cvvWrapperRef.current.classList.remove('focused');
        }
      }
    };

    window.addEventListener('message', handleFocus);
    window.addEventListener('message', handleBlur);

    return () => {
      window.removeEventListener('message', handleFocus);
      window.removeEventListener('message', handleBlur);
    };
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-2 lg:space-y-4">
        <Card className="bg-gradient-to-br from-card via-card/80 to-card/60 backdrop-blur-md border border-border shadow-lg">
          <CardHeader className="p-2.5 lg:p-6">
            <CardTitle className="flex items-center gap-1.5 lg:gap-2 text-[10px] lg:text-base font-black text-foreground uppercase tracking-wide">
              <CreditCard className="h-3.5 w-3.5 lg:h-5 lg:w-5" />
              Card Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 lg:space-y-4 p-2.5 lg:p-6 pt-0 lg:pt-0">
            {/* Card Number - Using Coinflow React SDK */}
            <div className="mt-0">
              <Label htmlFor="cardNumber" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Card Number</Label>
              <div 
                ref={cardWrapperRef}
                className="coinflow-card-wrapper mt-1" 
                style={{ 
                  height: '1.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid hsl(var(--casino-gold-dark) / 0.3)',
                  backgroundColor: 'rgba(15, 23, 42, 0.6)',
                  transition: 'all 0.2s',
                  overflow: 'hidden'
                }}
              >
                <CoinflowCardNumberInput
                  merchantId={merchantId}
                  env={environment === 'prod' ? 'prod' : 'sandbox'}
                  ref={coinflowCardNumberRef}
                  css={{
                    base: 'height: 1.75rem; width: 100%; border-radius: 0.5rem; border: none; background-color: transparent; color: rgb(252, 211, 77); padding: 0.25rem 0.5rem; font-size: 0.625rem; line-height: 1rem; outline: none; transition: all 0.2s; box-sizing: border-box; caret-color: rgb(252, 211, 77);',
                    focus: 'outline: none; border: none; box-shadow: none;',
                    error: 'border-color: hsl(0, 84%, 60%);',
                    cvv: {
                      base: 'height: 1.75rem; width: 100%; border-radius: 0.5rem; border: none; background-color: transparent; color: rgb(252, 211, 77); padding: 0.25rem 0.5rem; font-size: 0.625rem; line-height: 1rem; outline: none; transition: all 0.2s; box-sizing: border-box; caret-color: rgb(252, 211, 77);',
                      focus: 'outline: none; border: none; box-shadow: none;',
                      error: 'border-color: hsl(0, 84%, 60%);',
                    },
                  }}
                  origins={[window.location.origin]}
                />
              </div>
            </div>

            {/* Expiry Month and Year */}
            <div className="grid grid-cols-2 gap-1.5 lg:gap-4">
              <div>
                <Label htmlFor="expMonth" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Expiry Month</Label>
                <Input
                  id="expMonth"
                  type="text"
                  placeholder="MM"
                  value={cardData.expMonth}
                  onChange={(e) =>
                    setCardData({ ...cardData, expMonth: e.target.value })
                  }
                  maxLength={2}
                  disabled={isProcessing || !!challengeData}
                  required
                  className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="expYear" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Expiry Year</Label>
                <Input
                  id="expYear"
                  type="text"
                  placeholder="YY"
                  value={cardData.expYear}
                  onChange={(e) =>
                    setCardData({ ...cardData, expYear: e.target.value })
                  }
                  maxLength={2}
                  disabled={isProcessing || !!challengeData}
                  required
                  className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
                />
              </div>
            </div>

            {/* CVV */}
            <div>
              <Label htmlFor="cvv" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">CVV</Label>
              <div 
                ref={cvvWrapperRef}
                className="coinflow-cvv-wrapper mt-1"
                style={{
                  height: '1.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid hsl(var(--casino-gold-dark) / 0.3)',
                  backgroundColor: 'rgba(15, 23, 42, 0.6)',
                  transition: 'all 0.2s',
                  overflow: 'hidden'
                }}
              >
                <CoinflowCvvInput />
              </div>
            </div>

            {/* ZIP Code */}
            <div>
              <Label htmlFor="zipCode" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">ZIP Code</Label>
              <Input
                id="zipCode"
                type="text"
                placeholder="12345"
                value={cardData.zipCode}
                onChange={(e) =>
                  setCardData({ ...cardData, zipCode: e.target.value })
                }
                disabled={isProcessing || !!challengeData}
                required
                className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm bg-muted border-amber-500/30 text-amber-300"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-card via-card/80 to-card/60 backdrop-blur-md border border-border shadow-lg">
          <CardHeader className="p-2.5 lg:p-6">
            <CardTitle className="text-[10px] lg:text-base font-black text-foreground uppercase tracking-wide">Billing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 lg:space-y-4 p-2.5 lg:p-6 pt-0 lg:pt-0">
            {/* Name */}
            <div className="grid grid-cols-2 gap-1.5 lg:gap-4">
              <div>
                <Label htmlFor="firstName" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">First Name</Label>
                <Input
                  id="firstName"
                  value={billingInfo.firstName}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, firstName: e.target.value })
                  }
                  disabled={isProcessing || !!challengeData}
                  required
                  className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Last Name</Label>
                <Input
                  id="lastName"
                  value={billingInfo.lastName}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, lastName: e.target.value })
                  }
                  disabled={isProcessing || !!challengeData}
                  required
                  className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="address" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Address</Label>
              <Input
                id="address"
                value={billingInfo.address}
                onChange={(e) =>
                  setBillingInfo({ ...billingInfo, address: e.target.value })
                }
                disabled={isProcessing || !!challengeData}
                required
                className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm bg-muted/60 border-amber-500/30 text-amber-700"
              />
            </div>

            {/* City, State, ZIP */}
            <div className="grid grid-cols-3 gap-1.5 lg:gap-4">
              <div>
                <Label htmlFor="city" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">City</Label>
                <Input
                  id="city"
                  value={billingInfo.city}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, city: e.target.value })
                  }
                  disabled={isProcessing || !!challengeData}
                  required
                  className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="state" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">State</Label>
                <Input
                  id="state"
                  value={billingInfo.state}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, state: e.target.value })
                  }
                  disabled={isProcessing || !!challengeData}
                  required
                  className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="zip" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">ZIP</Label>
                <Input
                  id="zip"
                  value={billingInfo.zip}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, zip: e.target.value })
                  }
                  disabled={isProcessing || !!challengeData}
                  required
                  className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Card */}
        <div className="flex items-center space-x-1.5 lg:space-x-2">
          <Checkbox
            id="saveCard"
            checked={saveCard}
            onCheckedChange={(checked) => setSaveCard(checked === true)}
            disabled={isProcessing || !!challengeData}
            className="h-3.5 w-3.5 lg:h-4 lg:w-4"
          />
          <Label htmlFor="saveCard" className="text-[10px] lg:text-sm text-muted-foreground font-medium">
            Save card for future purchases
          </Label>
        </div>

        {/* Security Notice */}
        <div className="flex items-start gap-1.5 lg:gap-2 p-2 lg:p-3 bg-blue-500/20 border border-blue-400/30 rounded-lg">
          <Lock className="h-3 w-3 lg:h-4 lg:w-4 mt-0.5 text-blue-400 flex-shrink-0" />
          <p className="text-[9px] lg:text-xs text-blue-300/80">
            Your card information is securely tokenized and never stored on our servers.
          </p>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isProcessing || !!challengeData}
          className="w-full h-8 lg:h-11 bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 text-white font-black rounded-lg shadow-xl hover:shadow-2xl border border-green-400/30 hover:border-green-300/50 transition-all duration-200 text-[10px] lg:text-sm uppercase tracking-wide relative overflow-hidden group/btn"
        >
          {isProcessing ? (
            <span className="flex items-center gap-1.5 lg:gap-2">
              <span className="animate-spin">⏳</span>
              <span className="text-[9px] lg:text-sm">Processing...</span>
            </span>
          ) : (
            <span className="relative z-10">Pay ${(amount / 100).toFixed(2)}</span>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
        </Button>
      </form>

      {/* 3DS Challenge Modal */}
      {challengeData && (
        <ThreeDSChallengeModal
          url={challengeData.url}
          creq={challengeData.creq}
          transactionId={challengeData.transactionId}
          onChallengeComplete={handleChallengeComplete}
          onClose={() => {
            setChallengeData(null);
            setIsProcessing(false);
          }}
        />
      )}
    </>
  );
};

